<template>
  <Card>
    <CardHeader>
      <CardTitle class="tw-flex tw-items-center tw-gap-2">
        <FolderOpen class="tw-w-5 tw-h-5 tw-text-secondary-500" />
        <span class="tw-font-bold tw-text-[16px] tw-text-secondary-500 tw-m-0">
          {{ t('customer_management.main_data.title') }}
        </span>
      </CardTitle>
    </CardHeader>
    <form @submit="onSubmit">
      <CardContent class="tw-p-6">
        <div class="tw-grid tw-grid-cols-2 tw-gap-x-8 tw-gap-y-6">
          <!-- Left Column -->
          <div class="tw-space-y-1">
            <div>
              <Label
                for="customerName"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.forms.name') + ' *' }}
              </Label>
              <Input
                id="customerName"
                v-model="name"
                :placeholder="t('customer_management.forms.name_placeholder')"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': shouldShowError('name') }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('name') ? errors.name : '' }}
              </span>
            </div>

            <div>
              <Label
                for="legalName"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.forms.legal_name') + ' *' }}
              </Label>
              <Input
                id="legalName"
                v-model="legalName"
                :placeholder="t('customer_management.forms.name_placeholder')"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': shouldShowError('legalName') }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('legalName') ? errors.legalName : '' }}
              </span>
            </div>

            <div>
              <Label
                for="customerLimit"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.forms.customer_limit') + ' *' }}
              </Label>
              <ShadcnCurrencyInput
                id="customerLimit"
                v-model="customerLimit"
                :placeholder="t('customer_management.forms.limit_placeholder')"
                :class="
                  cn(
                    'tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
                    {
                      'tw-border-red-500': shouldShowError('customerLimit'),
                    }
                  )
                "
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('customerLimit') ? errors.customerLimit : '' }}
              </span>
            </div>

            <div>
              <Label
                for="automaticApprovalLimit"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.assign_team.auto_approval_limit_amount') + ' *' }}
              </Label>
              <ShadcnCurrencyInput
                id="automaticApprovalLimit"
                v-model="automaticApprovalLimit"
                :placeholder="t('customer_management.forms.limit_placeholder')"
                :class="
                  cn(
                    'tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
                    {
                      'tw-border-red-500': shouldShowError('automaticApprovalLimit'),
                    }
                  )
                "
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('automaticApprovalLimit') ? errors.automaticApprovalLimit : '' }}
              </span>
            </div>

            <div class="tw-flex tw-items-center tw-justify-between tw-pt-2">
              <div>
                <Label
                  for="restrictMode"
                  class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-block"
                >
                  {{ t('customer_management.titles.strict_mode') }}
                </Label>
                <p class="tw-text-sm tw-text-secondary-500 tw-mt-1">
                  {{ t('customer_management.forms.strict_mode_description') }}
                </p>
                <span class="tw-text-sm tw-text-gray-500 tw-mt-1">
                  {{
                    restrictMode
                      ? t('customer_management.forms.strict_mode_on')
                      : t('customer_management.forms.strict_mode_off')
                  }}
                </span>
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger as-child>
                    <div>
                      <Switch
                        id="restrictMode"
                        v-model="restrictMode"
                        class="tw-ml-4"
                        :disabled="restrictLocked"
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent v-if="restrictLocked">
                    {{ t('customer_management.forms.strict_mode_toggle_error') }}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>

          <!-- Right Column -->
          <div class="tw-space-y-1">
            <div>
              <Label
                for="address"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.main_data.address') }}
              </Label>
              <Input
                id="address"
                v-model="address"
                :placeholder="t('customer_management.forms.address_placeholder')"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': shouldShowError('address') }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('address') ? errors.address : '' }}
              </span>
            </div>

            <div>
              <Label
                for="email"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('common.titles.email') }}
              </Label>
              <Input
                id="email"
                v-model="email"
                type="email"
                :placeholder="t('customer_management.forms.email_placeholder')"
                class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                :class="{ 'tw-border-red-500': shouldShowError('email') }"
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('email') ? errors.email : '' }}
              </span>
            </div>

            <div>
              <Label
                for="phone"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('suppliers.titles.phone') }}
              </Label>
              <PhoneInput
                id="phone"
                v-model="phone"
                code="DE"
                :placeholder="t('customer_management.forms.phone_placeholder')"
                :class="
                  cn(
                    'tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none',
                    { 'tw-border-red-500': shouldShowError('phone') }
                  )
                "
              />
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{ shouldShowError('phone') ? errors.phone : '' }}
              </span>
            </div>

            <div>
              <Label
                for="financialLimitFrequency"
                class="tw-text-sm tw-font-medium tw-text-secondary-700 tw-mb-2 tw-block"
              >
                {{ t('customer_management.main_data.financial_limit_frequency') }}
              </Label>
              <Select v-model="financialLimitFrequency">
                <SelectTrigger
                  class="tw-w-full tw-h-12 tw-px-3 tw-border tw-border-secondary-200 tw-rounded-md tw-bg-secondary-50 tw-text-secondary-600 focus:tw-bg-white focus:tw-border-secondary-500 focus:tw-outline-none"
                  :class="{
                    'tw-border-red-500': shouldShowError('financialLimitFrequency'),
                  }"
                >
                  <SelectValue :placeholder="t('customer_management.forms.select_frequency')" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem :value="FinancialLimitFrequency.WEEKLY">
                    {{ t('customer_management.main_data.frequency_weekly') }}
                  </SelectItem>
                  <SelectItem :value="FinancialLimitFrequency.MONTHLY">
                    {{ t('customer_management.main_data.frequency_monthly') }}
                  </SelectItem>
                  <SelectItem :value="FinancialLimitFrequency.YEARLY">
                    {{ t('customer_management.main_data.frequency_yearly') }}
                  </SelectItem>
                  <SelectItem :value="FinancialLimitFrequency.ALL_TIME">
                    {{ t('customer_management.main_data.frequency_all_time') }}
                  </SelectItem>
                </SelectContent>
              </Select>
              <span class="tw-text-xs tw-text-red-500 tw-mt-1 tw-block tw-min-h-[20px]">
                {{
                  shouldShowError('financialLimitFrequency') ? errors.financialLimitFrequency : ''
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Logo Dropzone - Full Width -->
        <div class="tw-mt-6">
          <Dropzone
            id="logo"
            v-model="logoFile"
            :label="t('customer_management.main_data.company_logo')"
            accept="image/*"
            :accept-text="t('customer_management.main_data.logo_accept_text')"
            :upload-text="t('customer_management.main_data.upload_logo')"
            :description="t('customer_management.main_data.drag_drop_logo')"
            :error-message="logoError"
            :max-size="10"
            @fileSelected="handleFileSelected"
            @fileRemoved="handleFileRemoved"
            @error="handleFileError"
          />
        </div>
      </CardContent>
      <StepFooter
        v-if="!props.isEdit"
        :current-step="1"
        :total-steps="4"
        :loading="isSubmitting"
        @next="submitNext"
        @previous="emitPrevious"
        @finish="submitFinish"
        class="tw-px-6 tw-pb-6"
      />
    </form>

    <div v-if="props.isEdit" class="tw-flex tw-px-6 tw-pb-6 tw-justify-end">
      <SectionSaveButton
        :on-save="handleSectionSave"
        :is-edit="true"
        section-name="customer_management.main_data.title"
        :disabled="!meta.valid"
      />
    </div>
  </Card>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod';
import { FolderOpen } from 'lucide-vue-next';
import { useField, useForm } from 'vee-validate';
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as z from 'zod';
import { FinancialLimitFrequency } from '@/types/customer';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import ShadcnCurrencyInput from '@/components/ui/currency-input/ShadcnCurrencyInput.vue';
import { Dropzone } from '@/components/ui/dropzone';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import PhoneInput from '@/components/ui/phone-input/PhoneInput.vue';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import SectionSaveButton from './SectionSaveButton.vue';
import StepFooter from './StepFooter.vue';

const { t } = useI18n();

// Define the Zod schema
const mainDataSchema = z.object({
  name: z.string().min(1, t('customer_management.forms.name_required_error')),
  legalName: z.string().min(1, t('customer_management.forms.legal_name_required_error')),

  customerLimit: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .refine(val => val !== null && val !== undefined && val !== '', {
      message: t('customer_management.forms.customer_limit_required_error'),
    })
    .transform(val => {
      if (typeof val === 'string') return parseFloat(val);
      return val;
    })
    .refine(val => typeof val === 'number' && !isNaN(val), {
      message: t('common.messages.error'),
    })
    .refine(val => typeof val === 'number' && val >= 0, {
      message: t('customer_management.forms.customer_limit_invalid_error'),
    }),

  automaticApprovalLimit: z
    .union([z.number(), z.string(), z.null(), z.undefined()])
    .refine(val => val !== null && val !== undefined && val !== '', {
      message: t('customer_management.assign_team.auto_approval_limit_required'),
    })
    .transform(val => {
      if (typeof val === 'string') return parseFloat(val);
      return val;
    })
    .refine(val => typeof val === 'number' && !isNaN(val), {
      message: t('common.messages.error'),
    })
    .refine(val => typeof val === 'number' && val >= 0, {
      message: t('customer_management.forms.auto_approval_limit_invalid_error'),
    }),

  financialLimitFrequency: z.nativeEnum(FinancialLimitFrequency).optional(),

  address: z.string().optional(),
  email: z
    .union([z.string().email(t('customer_management.forms.email_invalid_error')), z.literal('')])
    .optional(),
  phone: z.string().optional(),

  restrictMode: z.boolean(),
});

type MainDataFormData = z.infer<typeof mainDataSchema>;

interface Props {
  data: MainDataFormData & { logo?: string | File | null };
  isEdit: boolean;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  onChange: [updates: Partial<MainDataFormData & { logo?: string | File | null }>];
  onSave: [];
  next: [];
  previous: [];
  finish: [];
}>();

const isSubmitting = ref(false);
const logoFile = ref<File | string | null>(props.data.logo || null);
const logoError = ref<string>();

// Setup vee-validate form with Zod schema
const { errors, meta, handleSubmit } = useForm({
  validationSchema: toTypedSchema(mainDataSchema),
  validateOnMount: false,
  initialValues: {
    name: props.data.name || '',
    legalName: props.data.legalName || '',
    customerLimit: props.data.customerLimit ?? undefined,
    automaticApprovalLimit: props.data.automaticApprovalLimit ?? undefined,
    financialLimitFrequency: props.data.financialLimitFrequency || FinancialLimitFrequency.ALL_TIME,
    address: props.data.address || '',
    email: props.data.email || '',
    phone: props.data.phone || '',
    restrictMode: props.data.restrictMode || false,
  },
});

const { value: name } = useField<string>('name');
const { value: legalName } = useField<string>('legalName');
const { value: customerLimit } = useField<number | string | null | undefined>('customerLimit');
const { value: automaticApprovalLimit } = useField<number | string | null | undefined>(
  'automaticApprovalLimit'
);
const { value: financialLimitFrequency } = useField<FinancialLimitFrequency | undefined>(
  'financialLimitFrequency'
);
const { value: address } = useField<string>('address');
const { value: email } = useField<string>('email');
const { value: phone } = useField<string>('phone');
const { value: restrictMode } = useField<boolean>('restrictMode');

// Lock restrict mode when editing and once enabled, it cannot be disabled
const restrictLocked = ref<boolean>(!!(props.isEdit && props.data.restrictMode));
watch(
  () => restrictMode.value,
  val => {
    if (props.isEdit && val === true) {
      restrictLocked.value = true;
    }
  }
);

const onSubmit = handleSubmit(() => {
  // ensure validation errors appear on submit
});

// Helper function to check if field should show error (only if touched)
const shouldShowError = (fieldName: string) => {
  if (fieldName === 'logo') {
    // Logo errors are handled by the dropzone component
    return false;
  }
  return errors.value[fieldName];
};

// Validate via vee-validate + zod before advancing
const submitNext = handleSubmit(() => {
  emit('next');
});

const submitFinish = handleSubmit(() => {
  emit('finish');
});

// Expose validate method so parent can trigger inline errors on step click
const validate = async () => {
  let ok = false;
  await handleSubmit(() => {
    ok = true;
  })();
  return ok;
};

defineExpose({ validate });

const emitPrevious = () => {
  emit('previous');
};

const handleSectionSave = () => {
  // Let parent know a save was requested (parent persists changes)
  emit('onSave');
  return {
    success: meta.value.valid,
    message: meta.value.valid ? 'Main data saved successfully' : 'Please fix validation errors',
  };
};

const handleFileSelected = () => {
  logoError.value = undefined;
  emitChanges();
};

const handleFileRemoved = () => {
  logoError.value = undefined;
  emitChanges();
};

const handleFileError = (message: string) => {
  logoError.value = message;
};

const emitChanges = () => {
  emit('onChange', {
    name: (name.value ?? '') as string,
    legalName: (legalName.value ?? '') as string,
    customerLimit:
      typeof customerLimit.value === 'number'
        ? customerLimit.value
        : typeof customerLimit.value === 'string'
        ? parseFloat(customerLimit.value) || 0
        : 0,
    automaticApprovalLimit:
      typeof automaticApprovalLimit.value === 'number'
        ? automaticApprovalLimit.value
        : typeof automaticApprovalLimit.value === 'string'
        ? parseFloat(automaticApprovalLimit.value) || 0
        : 0,
    financialLimitFrequency: financialLimitFrequency.value ?? FinancialLimitFrequency.ALL_TIME,
    address: (address.value ?? '') as string,
    email: (email.value ?? '') as string,
    phone: (phone.value ?? '') as string,
    restrictMode: (restrictMode.value ?? false) as boolean,
    logo: logoFile.value,
  });
};

// Watch for form field changes to emit updates
watch(
  [
    name,
    legalName,
    customerLimit,
    automaticApprovalLimit,
    financialLimitFrequency,
    address,
    email,
    phone,
    restrictMode,
    logoFile,
  ],
  () => {
    emitChanges();
  },
  { deep: true }
);
</script>
